import { Box, useMediaQuery } from "@mui/material";
import MuiSkeleton from "@mui/material/Skeleton";
import { useTheme } from "@mui/material/styles";
import React, { useContext, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router";

import { Course } from "../../components/Course/Course";
import { ErrorBoundary } from "../../components/Errors/ErrorBoundary";
import { Header } from "../../components/Header/Header";
import { Progress } from "../../components/Progress/Progress";
import { useViewport, View } from "../../components/Viewport/useViewport";
import { CourseContext } from "../../context/course-context";
import { NotesProvider } from "../../context/notes-context";
import { HelpAndSupportApp } from "../../haswidget/HelpAndSupportApp";
import { useAppState } from "../../hooks/useAppState";
import { useTestMode } from "../../hooks/useTestMode";
import { HelpAndSupport } from "./HelpAndSupport";
import { ContentContainer, ContentRight, ContentSkeleton, CourseWrapper, StyledPaperCourse } from "./Player.elements";
import ResizeWrapper from "./ResizeWrapper";
import { ViewportContent } from "./ViewportContent";

export function Player({ testMode = false }: Readonly<{ testMode?: boolean }>): React.ReactElement | null {
  const { courseId } = useParams<{ courseId: string }>();
  const { setCourse, setSlide, setModule } = useAppState();
  const { changeView } = useViewport();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const { setTestMode } = useTestMode();
  const course = useContext(CourseContext);

  useEffect(() => {
    setTestMode(testMode);
    if (testMode) setTimeout(() => changeView(View.Test));
  }, [testMode, setTestMode, changeView]);

  useEffect(() => {
    if (!testMode) {
      setSlide();
      setModule();
    }
    setCourse(testMode ? 1000 : Number(courseId));
  }, [courseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const CourseContent = (
    <StyledPaperCourse data-testid="course_content">
      {!isTablet && <Progress />}
      <CourseWrapper>
        <Course />
      </CourseWrapper>
      {isTablet && (
        <Box p={2}>
          <HelpAndSupport />
        </Box>
      )}
    </StyledPaperCourse>
  );

  const CourseContentSkeleton = (
    <ContentSkeleton data-testid="content_skeleton">
      <MuiSkeleton animation="wave" variant="rectangular" />
      <MuiSkeleton animation="wave" variant="rectangular" />
    </ContentSkeleton>
  );

  const getContentContainerClasses = () => {
    // Only skeleton when course isn't ready; right-side ViewportContent handles its own skeleton until module loads
    return course ? "" : "skeleton";
  };

  return (
    <NotesProvider>
      <Helmet>
        {course ? <title>{course.shortName || course.name}: SANS OnDemand</title> : <title>SANS OnDemand</title>}
      </Helmet>
      <Header />
      <ContentContainer
        className={getContentContainerClasses()}
        container
        spacing={{ xs: 0 }}
        id="content_container"
        flexDirection={isTablet ? "column" : "row"}
      >
        <ResizeWrapper disabled={isTablet}>{course ? CourseContent : CourseContentSkeleton}</ResizeWrapper>
        <ContentRight container>
          <ViewportContent />
        </ContentRight>
      </ContentContainer>
      <ErrorBoundary>
        {/* AI chat SME is here in Player component as it doesn't remount when resizing across 1000px (x axis) */}
        <HelpAndSupportApp />
      </ErrorBoundary>
    </NotesProvider>
  );
}
